import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table } from 'react-bootstrap';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import PropertyChat from './components/PropertyChat';
import './styles/chat-realestate.css';

// Intent-first hero section
const IntentFirstHero: React.FC = () => {
  const [chatOpen, setChatOpen] = useState(true);

  return (
    <section className="intent-hero py-5" style={{ background: '#f8f9fa', minHeight: '70vh' }}>
      <Container>
        <Row className="align-items-center">
          <Col lg={6}>
            <h1 className="display-4 fw-bold mb-3">Find homes by talking — not filtering.</h1>
            <p className="lead mb-4">
              chat.realestate uses AI to understand <em>why</em> you're moving—timeline, priorities, constraints—then surfaces homes that actually fit your life.
            </p>
            <p className="text-muted mb-4">No pressure. No sign-up required. Start with a conversation.</p>
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => setChatOpen(true)}
              className="mb-4"
            >
              Describe your move
            </Button>
          </Col>
          <Col lg={6}>
            {/* Chat component always visible */}
            <div className="chat-container shadow-lg rounded" style={{ height: '500px', background: 'white' }}>
              {/* PropertyChat component temporarily disabled for TypeScript fixes
              <PropertyChat 
                propertyId={null}
                isOpen={chatOpen}
                onClose={() => {}}
                variant="main" */}
                placeholder="Tell me about your ideal home and what's important to you..."
              {/* /> */}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

// Intent Before Inventory section
const IntentBeforeInventory: React.FC = () => {
  return (
    <section className="py-5 bg-white">
      <Container>
        <Row>
          <Col lg={8} className="mx-auto text-center">
            <h2 className="h1 mb-4">Intent Before Inventory</h2>
            <p className="lead">
              Most real estate sites show listings first and ask questions later.
            </p>
            <p className="lead">
              chat.realestate starts by understanding <em>why</em> you're moving—your timeline, priorities, and tradeoffs—then shows homes that make sense.
            </p>
            <p className="h5 text-primary mt-4">This isn't search. It's decision support.</p>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

// How This Is Different comparison table
const HowThisIsDifferent: React.FC = () => {
  return (
    <section className="py-5" style={{ background: '#f8f9fa' }}>
      <Container>
        <h2 className="text-center mb-5">How This Is Different</h2>
        <Row>
          <Col lg={10} className="mx-auto">
            <Table responsive bordered hover>
              <thead className="table-light">
                <tr>
                  <th>Traditional Home Search</th>
                  <th>chat.realestate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Filters & alerts</td>
                  <td>Natural conversation</td>
                </tr>
                <tr>
                  <td>Static results</td>
                  <td>Context-aware recommendations</td>
                </tr>
                <tr>
                  <td>Guessing motivation</td>
                  <td>Explicit intent understanding</td>
                </tr>
                <tr>
                  <td>Click behavior</td>
                  <td>Stated priorities & constraints</td>
                </tr>
                <tr>
                  <td>Leads sold</td>
                  <td>Ethical, user-controlled handoff</td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

// What AI Helps With section
const WhatAIHelpsWith: React.FC = () => {
  const helpItems = [
    "Understands your goals, timeline, and concerns",
    "Identifies tradeoffs you may not have considered",
    "Pulls relevant MLS results after context is clear",
    "Explains why a home fits (and why it doesn't)",
    "Helps you decide if and when to move forward"
  ];

  return (
    <section className="py-5 bg-white">
      <Container>
        <h2 className="text-center mb-5">What the AI Helps With</h2>
        <Row>
          <Col lg={8} className="mx-auto">
            <ul className="list-unstyled">
              {helpItems.map((item, idx) => (
                <li key={idx} className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-3">✓</span>
                  <span className="lead">{item}</span>
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

// Trust & Ethics Footer
const TrustEthicsRow: React.FC = () => {
  const trustItems = [
    { icon: "⚖️", text: "Fair Housing–aligned AI" },
    { icon: "🔒", text: "No demographic targeting" },
    { icon: "🧭", text: "Human-in-the-loop guidance" },
    { icon: "🤝", text: "You control when agents are involved" }
  ];

  return (
    <div className="trust-ethics-row py-3" style={{ background: '#e9ecef' }}>
      <Container>
        <Row className="text-center">
          {trustItems.map((item, idx) => (
            <Col md={3} key={idx} className="mb-2">
              <span className="me-2">{item.icon}</span>
              <span className="small">{item.text}</span>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

// Curated Results Panel (shown after context gathered)
interface CuratedResultsProps {
  hasEnoughContext: boolean;
  onRefinePreferences: () => void;
  onViewOnMap: () => void;
}

const CuratedResultsPanel: React.FC<CuratedResultsProps> = ({ 
  hasEnoughContext, 
  onRefinePreferences, 
  onViewOnMap 
}) => {
  if (!hasEnoughContext) return null;

  // Mock listings for prototype
  const mockListings = [
    {
      id: 1,
      address: "123 Main St",
      price: "$450,000",
      beds: 3,
      baths: 2,
      sqft: 1800,
      whyItMatches: "Near your preferred school district with the open floor plan you mentioned",
      image: "https://placehold.co/300x200/1e3a8a/ffffff?text=Home+1"
    },
    {
      id: 2,
      address: "456 Oak Ave",
      price: "$425,000",
      beds: 3,
      baths: 2.5,
      sqft: 2000,
      whyItMatches: "Quiet neighborhood with home office space and within your budget",
      image: "https://placehold.co/300x200/1e3a8a/ffffff?text=Home+2"
    },
    {
      id: 3,
      address: "789 Elm Dr",
      price: "$475,000",
      beds: 4,
      baths: 3,
      sqft: 2200,
      whyItMatches: "Extra bedroom for guests, pool included as you requested",
      image: "https://placehold.co/300x200/1e3a8a/ffffff?text=Home+3"
    }
  ];

  return (
    <section className="py-5" style={{ background: '#f8f9fa' }}>
      <Container>
        <h2 className="text-center mb-4">Homes That Match Your Criteria</h2>
        <p className="text-center text-muted mb-5">Based on our conversation, here are homes that fit your needs:</p>
        
        <Row>
          {mockListings.map(listing => (
            <Col md={4} key={listing.id} className="mb-4">
              <div className="card h-100 shadow-sm">
                <img src={listing.image} className="card-img-top" alt={listing.address} />
                <div className="card-body">
                  <h5 className="card-title">{listing.address}</h5>
                  <p className="h4 text-primary">{listing.price}</p>
                  <p className="text-muted">
                    {listing.beds} beds • {listing.baths} baths • {listing.sqft} sqft
                  </p>
                  <div className="alert alert-info small">
                    <strong>Why it matches:</strong> {listing.whyItMatches}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
        
        <div className="text-center mt-4">
          <Button variant="outline-primary" className="me-3" onClick={onRefinePreferences}>
            Refine preferences
          </Button>
          <Button variant="primary" onClick={onViewOnMap}>
            View on map
          </Button>
        </div>
      </Container>
    </section>
  );
};

const HomePageV2: React.FC = () => {
  const [hasEnoughContext, setHasEnoughContext] = useState(false);

  // Simulate context gathering (in real app, this would come from chat analysis)
  useEffect(() => {
    const timer = setTimeout(() => {
      // For demo: show results after 10 seconds
      setHasEnoughContext(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleRefinePreferences = () => {
    // Focus back on chat
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewOnMap = () => {
    // Navigate to map with filters applied
    console.log('Navigate to filtered map view');
  };

  return (
    <div className="chat-realestate-home-v2">
      <NavigationHeader />
      
      {/* Intent-first hero with prominent chat */}
      <IntentFirstHero />
      
      {/* Intent Before Inventory philosophy */}
      <IntentBeforeInventory />
      
      {/* How This Is Different comparison */}
      <HowThisIsDifferent />
      
      {/* What AI Helps With */}
      <WhatAIHelpsWith />
      
      {/* Curated Results (appears after context gathered) */}
      <CuratedResultsPanel 
        hasEnoughContext={hasEnoughContext}
        onRefinePreferences={handleRefinePreferences}
        onViewOnMap={handleViewOnMap}
      />
      
      {/* Trust & Ethics Row */}
      <TrustEthicsRow />
      
      <FooterWithPolicies />
    </div>
  );
};

export default HomePageV2;